import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useHasPermission } from '@/entities/profile';
import { User, useCreateUser, useUpdateUser } from '@/entities/users';
import { useAppNavigate } from '@/shared/lib';
import { UserFormValues } from './types';
import { useUserSchema } from './validation';
import { useFormGuardActions } from '@/entities/formGuard';

type Params = {
  initial?: User;
  onSuccess?: () => void;
};

export const useUserForm = (params: Params) => {
  const { initial, onSuccess } = params;
  const navigate = useAppNavigate();
  const allowAdmin = useHasPermission('ADMIN_USER');
  const isCreating = !initial;
  const schema = useUserSchema(isCreating);
  const { resetFormGuard } = useFormGuardActions();

  const defaults: Partial<UserFormValues> = useMemo(
    () => ({
      firstName: initial?.firstName ?? '',
      lastName: initial?.lastName ?? '',
      middleName: initial?.middleName ?? '',
      email: initial?.email ?? '',
      roleCode: initial?.role?.code ?? '',
      isActive: initial?.isActive ?? true,
      userGroups: initial?.userGroups?.map((g) => ({ id: g.id })) ?? [],
      password: '',
    }),
    [initial],
  );

  const form = useForm<UserFormValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    form.reset(defaults);
  }, [form, defaults]);

  const { mutateAsync: createUser, isPending: isCreatingPending } = useCreateUser();
  const { mutateAsync: updateUser, isPending: isUpdating } = useUpdateUser();

  const isEdit = allowAdmin && !!initial;
  const isDisabled = !allowAdmin;
  const isLoading = isCreatingPending || isUpdating;

  const onSubmit = form.handleSubmit(async (values) => {
    const userGroups = values.userGroups ?? [];

    if (isEdit && initial) {
      await updateUser({
        id: initial.id,
        firstName: values.firstName,
        lastName: values.lastName,
        middleName: values.middleName || null,
        email: values.email,
        role: values.roleCode ? { code: values.roleCode } : null,
        isActive: values.isActive,
        userGroups,
      });
      resetFormGuard();
    } else if (values.password) {
      const created = await createUser({
        firstName: values.firstName,
        lastName: values.lastName,
        middleName: values.middleName || null,
        email: values.email,
        role: { code: values.roleCode },
        isActive: values.isActive,
        password: values.password,
        userGroups,
      });
      resetFormGuard();
      navigate('/users/lists/:id', { id: created.id.toString() }, { replace: true });
    }

    onSuccess?.();
  });

  // Handle title (name) change on blur - save immediately if editing
  const handleTitleSave = async (value: string) => {
    form.setValue('firstName', value);

    if (isEdit && initial && value !== initial.firstName) {
      await updateUser({
        id: initial.id,
        firstName: value,
      });
    }
  };

  return {
    defaultValues: defaults,
    form,
    isEdit,
    isCreating,
    isLoading,
    isDisabled,
    onSubmit,
    handleTitleSave,
  } as const;
};
